import { useMemo, useState } from 'react';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguageContext } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  APP_LANGUAGE_DEFINITIONS,
  DEFAULT_LANGUAGE,
  type LanguageCode,
  isLanguageCode,
} from '@/localization/languages';
import { cn } from '@/lib/utils';

const RECENT_LANGUAGES_STORAGE_KEY = 'sposa_recent_languages';
const MAX_RECENT_LANGUAGES = 5;

type PickerVariant = 'compact' | 'floating' | 'icon';

interface LanguagePickerProps {
  variant?: PickerVariant;
  className?: string;
}

interface LanguageOption {
  code: LanguageCode;
  primaryLabel: string;
  secondaryLabel?: string;
  searchText: string;
}

function normalizeLanguageCode(value: string): string {
  const shortCode = value.toLowerCase().split(/[-_]/)[0];
  return shortCode === 'uk' ? 'ua' : shortCode;
}

function readRecentLanguages(): LanguageCode[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = localStorage.getItem(RECENT_LANGUAGES_STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry): entry is LanguageCode => typeof entry === 'string' && isLanguageCode(entry));
  } catch {
    return [];
  }
}

function writeRecentLanguages(languages: LanguageCode[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(RECENT_LANGUAGES_STORAGE_KEY, JSON.stringify(languages));
}

function detectBrowserLanguage(): LanguageCode | null {
  if (typeof window === 'undefined') return null;

  const browserLanguages = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language].filter(Boolean);

  for (const browserLanguage of browserLanguages) {
    const normalized = normalizeLanguageCode(browserLanguage);
    if (isLanguageCode(normalized)) {
      return normalized;
    }
  }

  return null;
}

function getRegionLabel(locale: string): string | undefined {
  const parts = locale.split('-');
  if (parts.length < 2) return undefined;

  const regionCode = parts[1].toUpperCase();

  try {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return regionNames.of(regionCode) || regionCode;
  } catch {
    return regionCode;
  }
}

function LanguageSection({
  title,
  items,
  currentLanguage,
  onSelect,
  currentLabel,
}: {
  title: string;
  items: LanguageOption[];
  currentLanguage: LanguageCode;
  onSelect: (code: LanguageCode) => void;
  currentLabel: string;
}) {
  if (items.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="space-y-1">
        {items.map((item) => {
          const isCurrent = item.code === currentLanguage;

          return (
            <button
              key={item.code}
              type="button"
              onClick={() => onSelect(item.code)}
              className={cn(
                'flex min-h-12 w-full items-center justify-between rounded-md px-3 py-3 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isCurrent && 'bg-accent/60'
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-[15px] font-medium leading-tight">{item.primaryLabel}</p>
                {item.secondaryLabel && (
                  <p className="truncate text-xs text-muted-foreground">{item.secondaryLabel}</p>
                )}
              </div>

              <div className="ml-3 flex items-center gap-2">
                {isCurrent && (
                  <Badge variant="secondary" className="h-5 px-2 text-[10px] font-medium">
                    {currentLabel}
                  </Badge>
                )}
                {isCurrent && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function LanguagePicker({ variant = 'compact', className }: LanguagePickerProps) {
  const { language, setLanguage } = useLanguageContext();
  const { t } = useTranslation('portal');
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [recentLanguages, setRecentLanguages] = useState<LanguageCode[]>(() => readRecentLanguages());

  const browserLanguage = useMemo(() => detectBrowserLanguage(), []);

  const languages = useMemo<LanguageOption[]>(() => {
    return [...APP_LANGUAGE_DEFINITIONS]
      .sort((left, right) => left.nativeName.localeCompare(right.nativeName))
      .map((languageDefinition) => {
        const selectorKey = (languageDefinition as { selectorKey?: string }).selectorKey;
        const primaryLabel = selectorKey
          ? t(selectorKey, { defaultValue: languageDefinition.nativeName })
          : languageDefinition.nativeName;

        const secondaryParts: string[] = [];
        if (primaryLabel !== languageDefinition.englishName) {
          secondaryParts.push(languageDefinition.englishName);
        }

        const regionLabel = getRegionLabel(languageDefinition.locale);
        if (regionLabel) {
          secondaryParts.push(regionLabel);
        }

        const secondaryLabel = secondaryParts.length > 0 ? secondaryParts.join(' — ') : undefined;

        const searchText = [
          languageDefinition.code,
          languageDefinition.locale,
          languageDefinition.nativeName,
          languageDefinition.englishName,
          primaryLabel,
          secondaryLabel,
        ]
          .join(' ')
          .toLowerCase();

        return {
          code: languageDefinition.code,
          primaryLabel,
          secondaryLabel,
          searchText,
        };
      });
  }, [t]);

  const optionsByCode = useMemo(() => {
    return new Map(languages.map((languageOption) => [languageOption.code, languageOption]));
  }, [languages]);

  const currentLanguage = optionsByCode.get(language) ?? optionsByCode.get(DEFAULT_LANGUAGE) ?? languages[0];

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return languages;
    return languages.filter((languageOption) => languageOption.searchText.includes(normalizedQuery));
  }, [languages, query]);

  const filteredCodeSet = useMemo(() => new Set(filteredOptions.map((languageOption) => languageOption.code)), [filteredOptions]);

  const suggestedCodes = useMemo(() => {
    const codes: LanguageCode[] = [];
    const addCode = (code: LanguageCode | null | undefined) => {
      if (!code) return;
      if (!codes.includes(code)) codes.push(code);
    };

    addCode(language);
    addCode(browserLanguage);
    addCode(DEFAULT_LANGUAGE);

    return codes;
  }, [browserLanguage, language]);

  const suggestedOptions = useMemo(
    () => suggestedCodes
      .filter((code) => filteredCodeSet.has(code))
      .map((code) => optionsByCode.get(code))
      .filter((option): option is LanguageOption => Boolean(option)),
    [filteredCodeSet, optionsByCode, suggestedCodes]
  );

  const suggestedCodeSet = useMemo(() => new Set(suggestedOptions.map((option) => option.code)), [suggestedOptions]);

  const recentOptions = useMemo(
    () => recentLanguages
      .filter((code) => filteredCodeSet.has(code) && !suggestedCodeSet.has(code))
      .map((code) => optionsByCode.get(code))
      .filter((option): option is LanguageOption => Boolean(option)),
    [filteredCodeSet, optionsByCode, recentLanguages, suggestedCodeSet]
  );

  const recentCodeSet = useMemo(() => new Set(recentOptions.map((option) => option.code)), [recentOptions]);

  const allOptions = useMemo(
    () => filteredOptions.filter(
      (option) => !suggestedCodeSet.has(option.code) && !recentCodeSet.has(option.code)
    ),
    [filteredOptions, recentCodeSet, suggestedCodeSet]
  );

  const applyLanguage = (nextLanguage: LanguageCode, previousLanguage?: LanguageCode, withToast = true) => {
    setLanguage(nextLanguage);

    const nextRecent = [nextLanguage, ...recentLanguages.filter((recentCode) => recentCode !== nextLanguage)].slice(0, MAX_RECENT_LANGUAGES);
    setRecentLanguages(nextRecent);
    writeRecentLanguages(nextRecent);

    if (withToast && previousLanguage && previousLanguage !== nextLanguage) {
      const selectedLanguageName = optionsByCode.get(nextLanguage)?.primaryLabel ?? nextLanguage.toUpperCase();
      toast.success(t('language.picker.changed', { language: selectedLanguageName }), {
        action: {
          label: t('language.picker.undo'),
          onClick: () => applyLanguage(previousLanguage, undefined, false),
        },
      });
    }
  };

  const handleLanguageSelect = (code: LanguageCode) => {
    if (code !== language) {
      applyLanguage(code, language, true);
    }

    setOpen(false);
    setQuery('');
  };

  const triggerLabel = currentLanguage?.primaryLabel ?? language.toUpperCase();

  const triggerButton = (
    <Button
      variant={variant === 'floating' ? 'outline' : 'ghost'}
      size="sm"
      className={cn(
        'gap-2',
        variant === 'icon' && 'h-11 w-11 rounded-full p-0',
        variant === 'compact' && 'h-9 max-w-[180px] px-2.5',
        variant === 'floating' && 'h-9 max-w-[220px] bg-background/95 backdrop-blur-sm',
        className
      )}
      aria-label={t('language.picker.open')}
    >
      <Globe className="h-4 w-4 shrink-0" />
      {variant !== 'icon' && (
        <>
          <span className="truncate text-sm">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </>
      )}
    </Button>
  );

  const pickerBody = (
    <>
      <div className="px-6 pb-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('language.picker.searchPlaceholder')}
            className="pl-10"
            autoFocus={!isMobile}
          />
        </div>
      </div>

      <ScrollArea className={cn('px-4 pb-6', isMobile ? 'max-h-[calc(100dvh-170px)]' : 'max-h-[60vh]')}>
        <div className="space-y-5">
          <LanguageSection
            title={t('language.picker.suggested')}
            items={suggestedOptions}
            currentLanguage={language}
            onSelect={handleLanguageSelect}
            currentLabel={t('language.picker.current')}
          />

          <LanguageSection
            title={t('language.picker.recent')}
            items={recentOptions}
            currentLanguage={language}
            onSelect={handleLanguageSelect}
            currentLabel={t('language.picker.current')}
          />

          <LanguageSection
            title={t('language.picker.all')}
            items={allOptions}
            currentLanguage={language}
            onSelect={handleLanguageSelect}
            currentLabel={t('language.picker.current')}
          />

          {filteredOptions.length === 0 && (
            <p className="px-2 py-6 text-sm text-muted-foreground">{t('language.picker.noResults')}</p>
          )}
        </div>
      </ScrollArea>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setQuery('');
        }}
      >
        <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
        <DrawerContent className="inset-0 mt-0 h-[100dvh] max-h-none rounded-none border-0 bg-background">
          <DrawerHeader className="border-b px-6 pb-3 pt-6 text-left">
            <DrawerTitle>{t('language.picker.title')}</DrawerTitle>
            <DrawerDescription>{t('language.picker.description')}</DrawerDescription>
          </DrawerHeader>
          {pickerBody}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setQuery('');
      }}
    >
      <DialogTrigger asChild>{triggerButton}</DialogTrigger>

      <DialogContent className="w-[95vw] max-w-2xl gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 text-left">
          <DialogTitle>{t('language.picker.title')}</DialogTitle>
          <DialogDescription>{t('language.picker.description')}</DialogDescription>
        </DialogHeader>
        {pickerBody}
      </DialogContent>
    </Dialog>
  );
}
